import { parseCSV } from '@/lib/parsers/csv';

// ── Row shapes ────────────────────────────────────────────────────────────────

export interface WIPRow {
  [key: string]: string;
  WO_Number: string;
  End_Item: string;
  NSN_NIIN: string;
  Platform: string;
  Shop_Cost_Center: string;
  Customer: string;
  Priority: string;
  Induction_Date: string;
  ECD: string;
  Status: string;
  Percent_Complete: string;
  Days_In_WIP: string;
  Days_In_Current_Status: string;
  AWP_Flag: string;
  Past_ECD: string;
  Lead_Artisan: string;
}

export interface SupplyRow {
  [key: string]: string;
  Line_ID: string;
  WO_Number: string;
  Platform: string;
  Part_Nomenclature: string;
  NSN_NIIN: string;
  Qty_Required: string;
  Qty_On_Hand: string;
  Backorder_Qty: string;
  Requisition_Date: string;
  EDD: string;
  Lead_Time_Days: string;
  Source: string;
  Supply_Status: string;
  Past_EDD: string;
}

export interface ScheduleRow {
  [key: string]: string;
  Month: string;
  Product_Line: string;
  Planned_Output_Units: string;
  Actual_Output_Units: string;
  Target_TAT_Days: string;
  Actual_TAT_Days: string;
  Capacity_Hours: string;
  Earned_Hours: string;
  Schedule_Adherence_Pct: string;
}

export type DepotFileType = 'wip' | 'supply' | 'schedule' | 'unknown';

// ── File-type detection by header signature ───────────────────────────────────

const HEADER_SIGNATURES: Record<DepotFileType, string[]> = {
  wip: ['WO_Number', 'End_Item', 'Shop_Cost_Center', 'AWP_Flag', 'Past_ECD'],
  supply: ['Line_ID', 'WO_Number', 'Part_Nomenclature', 'Supply_Status', 'Past_EDD'],
  schedule: ['Month', 'Product_Line', 'Planned_Output_Units', 'Schedule_Adherence_Pct'],
  unknown: [],
};

export function detectFileType(headers: string[]): DepotFileType {
  const headerSet = new Set(headers.map((h) => h.trim()));
  for (const [type, sigs] of Object.entries(HEADER_SIGNATURES) as [DepotFileType, string[]][]) {
    if (type === 'unknown') continue;
    if (sigs.every((s) => headerSet.has(s))) return type;
  }
  return 'unknown';
}

// ── Parsed dataset ────────────────────────────────────────────────────────────

// Use Record<string,string>[] for JSON round-trip safety;
// callers cast to the narrower typed shapes after detection.
export interface DepotDataset {
  wip: Record<string, string>[] | null;
  supply: Record<string, string>[] | null;
  schedule: Record<string, string>[] | null;
}

// ── localStorage persistence ──────────────────────────────────────────────────

const LS_KEY = 'depot-data-lab-v1';

export function saveDataset(ds: DepotDataset): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(ds));
  } catch {
    // localStorage may be unavailable (SSR, private mode quota)
  }
}

export function loadDataset(): DepotDataset | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as DepotDataset;
  } catch {
    return null;
  }
}

export function clearDataset(): void {
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    // ignore
  }
}

// ── Parse file text into the correct typed rows ───────────────────────────────

export function parseDepotFile(
  text: string
): { type: DepotFileType; rows: Record<string, string>[] } {
  const rows = parseCSV(text);
  if (rows.length === 0) return { type: 'unknown', rows: [] };
  const headers = Object.keys(rows[0]);
  const type = detectFileType(headers);
  return { type, rows };
}

// ── Fetch the bundled sample files from /downloads ───────────────────────────

export const SAMPLE_FILES: { filename: string; label: string }[] = [
  { filename: 'Depot_WIP_NOTIONAL.csv', label: 'Depot WIP' },
  { filename: 'Depot_Supply_AWP_NOTIONAL.csv', label: 'Supply / AWP' },
  { filename: 'Depot_Schedule_Throughput_NOTIONAL.csv', label: 'Schedule & Throughput' },
];

export async function loadSampleDataset(): Promise<DepotDataset> {
  const ds: DepotDataset = { wip: null, supply: null, schedule: null };
  await Promise.all(
    SAMPLE_FILES.map(async ({ filename }) => {
      const res = await fetch(`/downloads/${filename}`);
      if (!res.ok) return;
      const text = await res.text();
      const { type, rows } = parseDepotFile(text);
      if (type === 'wip') ds.wip = rows;
      if (type === 'supply') ds.supply = rows;
      if (type === 'schedule') ds.schedule = rows;
    })
  );
  return ds;
}

// ── Derived stats helpers ─────────────────────────────────────────────────────

export function countBy<T extends Record<string, string>>(
  rows: T[],
  key: keyof T
): { label: string; count: number }[] {
  const map = new Map<string, number>();
  for (const row of rows) {
    const val = row[key] ?? 'Unknown';
    map.set(val, (map.get(val) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

export function groupBy<T extends Record<string, string>>(
  rows: T[],
  key: keyof T
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const row of rows) {
    const val = row[key] ?? 'Unknown';
    if (!map.has(val)) map.set(val, []);
    map.get(val)!.push(row);
  }
  return map;
}
